import { ELEMENTS, resolveAppUrl } from '@ofself/shell';
import { isSafeRedirectUrl } from './redirectSafety';
import { resolveNext } from './loginRedirect';

// THE JOIN FLOW — joining ofSelf, from Nucleus's front door or from an app.
//
// An app never creates accounts: Caliber sends a signed-out person to
// /authorize, the forced logout carries that whole address to /login?next=,
// and "Create your ofSelf account" carries it on to /register. The steps after
// the account (photo, recovery codes) keep `next` too, so the person ends where
// they started — back at the app's /authorize, which for a first account asks
// nothing and opens the app (GrantRoute's firstTime).

const FRESH = 'ofself.join.fresh';       // the user id this tab just created
const CODES = 'ofself.join.codes';        // the first-login recovery codes, until saved

const read = (store, key) => { try { return store.getItem(key); } catch { return null; } };
const write = (store, key, value) => {
  try { value == null ? store.removeItem(key) : store.setItem(key, value); } catch { /* private mode */ }
};

/** The `next` a join page was handed, if it is one we may follow. */
export function joinNext(searchParams) {
  const raw = resolveNext(searchParams.get('next'));
  return raw && isSafeRedirectUrl(raw) ? raw : null;
}

/** A path with `next` carried on, when there is one. */
export function withNext(path, next) {
  return next ? `${path}?next=${encodeURIComponent(next)}` : path;
}

/**
 * The ofSelf app a `next` leads back to, read from the shell's own registry
 * (ELEMENTS) by the host of its redirect_uri — never from anything the URL
 * claims about itself, so a link cannot dress itself up as Caliber.
 */
export function appForNext(next) {
  if (!next) return null;
  let target;
  try {
    const url = new URL(next, window.location.origin);
    const back = url.searchParams.get('redirect_uri') || url.searchParams.get('return_url');
    target = new URL(back || url.href, window.location.origin);
  } catch {
    return null;
  }
  if (target.origin === window.location.origin) return null;
  // The registry's address, or this browser's own dev override of it
  // (resolveAppUrl: `paradigm_dev_app_urls`, set by hand in one's own
  // browser, so a link cannot supply it): Caliber on localhost:5180 is Caliber.
  const hostOf = (u) => { try { return new URL(u).host; } catch { return null; } };
  return ELEMENTS.find((el) => el.id !== 'nucleus'
    && [el.url, resolveAppUrl(el)].some((u) => hostOf(u) === target.host)) || null;
}

/** Leave the join flow for `next` (an app, or a Nucleus path), or for home. */
export function finishJoin(next, navigate) {
  write(sessionStorage, CODES, null);
  if (next && /^https?:\/\//.test(next)) window.location.href = next;
  else navigate(next || '/home', { replace: true });
}

// Set while sign-up signs the person in. Held in memory, not sessionStorage: a
// reload mid-sign-up must not leave PublicRoute waving signed-in people through.
let inFlight = false;
export const joinInFlight = () => inFlight;
export const setJoinInFlight = (on) => { inFlight = !!on; };

/** This tab created `userId`'s account: their first app grant asks nothing. */
export const markFresh = (userId) => write(sessionStorage, FRESH, userId ? String(userId) : null);
export const isFresh = (userId) => !!userId && read(sessionStorage, FRESH) === String(userId);
export const clearFresh = () => write(sessionStorage, FRESH, null);

/** The recovery codes survive a reload anywhere in the join (kept the moment
 *  they are minted, AuthContext), and nothing longer. */
export const keepCodes = (codes) => write(sessionStorage, CODES, codes?.length ? JSON.stringify(codes) : null);
export function keptCodes() {
  try {
    const parsed = JSON.parse(read(sessionStorage, CODES) || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

// THE PHONE STEP (SIGNUP_MODE=phone): an unverified account's sign-in answers
// PHONE_VERIFICATION_REQUIRED with a short-lived token. It travels to
// /join/phone in router state, never in storage; `fresh` says this tab just
// made the account, `name` is the name the profile should take once signed in.
export const isPhoneVerificationRequired = (err) => err?.code === 'PHONE_VERIFICATION_REQUIRED'
  && !!err.verification_token;
export const phoneStepState = (err, extra = {}) => ({
  verificationToken: err.verification_token,
  expiresAt: Date.now() + (Number(err.expires_in) || 900) * 1000,
  ...extra,
});

// The password just typed, held in memory only (as joinInFlight is) so the
// phone step's sign-in can unlock the account's keys. Never in router state or
// storage. A reload loses it, and the phone step then asks for sign-in again
// rather than finishing without the keys (JoinPhone).
let password = null;
export const holdPassword = (p) => { password = p || null; };
export const heldPassword = () => password;
